import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import * as Sentry from '@sentry/react';
import { Loader2, Sparkles, X } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import FadeIn from '@/components/animations/FadeIn';
import { CoursePullPanel } from '@/components/CoursePullPanel/CoursePullPanel';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { EditorType } from '@/Enums/enum';
import useAiSuggest from '@/hooks/useAiSuggest';
import { StyledP } from '@/Shared/Typography/typography';
import { useCourseEditStore } from '@/stores/courseEditStore';

import CourseDiff from '../CourseDiff';
import CourseEditContext from '../CourseEditContext';

import DraggableOutcome from './DraggableOutcome';

const CourseOutcomeTab = () => {
	const {
		courseData,
		updateOutcomeOrder,
		addOutcome,
		addOutcomeWithText,
		canEdit,
		submitCourse,
		courseDraftData,
		isApprovalMode,
	} = useContext(CourseEditContext);

	const { courses_outcomes: courseOutcome } = courseData;
	const { courses_outcomes: courseDraftOutcome } = courseDraftData;
	const { handleSubmit } = useForm({ shouldUnregister: true });

	const addNewOutcome = () => addOutcome();

	const renderHeader = () => {
		if (isApprovalMode && !courseDraftOutcome) {
			return <StyledP mb="32px">No outcomes are present.</StyledP>;
		}

		if (isApprovalMode && courseDraftOutcome && courseDraftOutcome?.length === 0) {
			return (
				<StyledP ml="16px" mb="32px">
					No outcomes are present.
				</StyledP>
			);
		}

		if (courseDraftOutcome?.length === 0) {
			return (
				<StyledP data-testid="add-outcome-message" mb="32px">
					Add the first course outcome.
				</StyledP>
			);
		}

		if (isApprovalMode) {
			return <></>;
		}

		return <></>;
	};

	const onSubmit = () => submitCourse();
	const { updateCommentTypes } = useCourseEditStore();

	const {
		suggest: generateOutcomes,
		loading: aiLoading,
		suggestions: aiSuggestions,
		panelOpen: aiPanelOpen,
		setPanelOpen: setAiPanelOpen,
		markAccepted,
		error: aiError,
	} = useAiSuggest('generate_outcomes');

	const [checkedSuggestions, setCheckedSuggestions] = useState<Record<number, boolean>>({});
	const [pullPanelOpen, setPullPanelOpen] = useState(false);

	const existingOutcomeTexts = useMemo(
		() =>
			(courseDraftOutcome ?? [])
				.map((o: CoursesOutcomesEntity) => o.outcome?.outcome_text?.trim() ?? '')
				.filter(Boolean),
		[courseDraftOutcome]
	);

	// Pre-check all suggestions when new ones arrive
	useEffect(() => {
		const initial: Record<number, boolean> = {};
		aiSuggestions.forEach((_, i) => {
			initial[i] = true;
		});
		setCheckedSuggestions(initial);
	}, [aiSuggestions]);

	const handleAddSelected = () => {
		aiSuggestions.forEach((text, i) => {
			if (checkedSuggestions[i] && typeof text === 'string') {
				addOutcomeWithText(text);
			}
		});
		markAccepted();
		setAiPanelOpen(false);
	};

	// Sort outcomes by order for consistent display
	const sortedOutcomes = useMemo(() => {
		return [...(courseDraftOutcome ?? [])].sort(
			(a: CoursesOutcomesEntity, b: CoursesOutcomesEntity) =>
				a.outcome.outcome_order - b.outcome.outcome_order
		);
	}, [courseDraftOutcome]);

	// Handle drag end with @dnd-kit
	const onDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;

			if (!over || active.id === over.id) {
				return;
			}

			const oldIndex = sortedOutcomes.findIndex(
				(outcome: CoursesOutcomesEntity) => outcome.courses_outcomes_id === active.id
			);
			const newIndex = sortedOutcomes.findIndex(
				(outcome: CoursesOutcomesEntity) => outcome.courses_outcomes_id === over.id
			);

			// Validate indices before proceeding
			if (oldIndex === -1 || newIndex === -1) {
				Sentry.captureMessage('Invalid drag indices in outcome reordering', {
					level: 'warning',
					extra: { oldIndex, newIndex },
				});
				return;
			}

			const reorderedOutcomes = arrayMove(sortedOutcomes, oldIndex, newIndex);

			// Update the order property for each outcome
			const updatedOutcomes = reorderedOutcomes.map(
				(outcome: CoursesOutcomesEntity, index: number) => ({
					...outcome,
					outcome: {
						...outcome.outcome,
						outcome_order: index,
					},
				})
			);

			updateOutcomeOrder(updatedOutcomes);
		},
		[sortedOutcomes, updateOutcomeOrder]
	);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	// Extract IDs for SortableContext
	const outcomeIds = useMemo(() => {
		return sortedOutcomes.map((outcome: CoursesOutcomesEntity) => outcome.courses_outcomes_id);
	}, [sortedOutcomes]);

	useEffect(() => {
		updateCommentTypes([EditorType.OUTCOME]);
	}, [updateCommentTypes]);

	return (
		<>
			<FadeIn style={{ width: '100%' }}>
				{/* Tab-level AI: Generate Outcomes */}
				{canEdit && !isApprovalMode && (
					<div className="flex justify-end gap-2 mb-4 flex-wrap">
						<Button
							type="button"
							variant="secondary"
							size="xs"
							onClick={() => setPullPanelOpen(true)}
							disabled={!courseData?.course_id}
							aria-label="Copy outcomes from another course at your school into this course"
						>
							Copy from another course
						</Button>
						<Button
							type="button"
							variant="secondary"
							size="xs"
							onClick={generateOutcomes}
							disabled={aiLoading}
						>
							{aiLoading ? (
								<Loader2 size={12} className="animate-spin mr-0.5" />
							) : (
								<Sparkles size={12} className="mr-0.5" />
							)}
							{aiLoading ? 'Generating…' : 'Generate Outcomes'}
						</Button>
					</div>
				)}

				{/* AI error */}
				{aiError && <p className="text-[12px] text-destructive mb-4">{aiError}</p>}

				{pullPanelOpen && courseData?.course_id && (
					<CoursePullPanel
						mode="outcomes"
						currentCourseId={courseData.course_id}
						existingTexts={existingOutcomeTexts}
						label="Outcomes"
						onAdd={(texts) => {
							for (const t of texts) {
								addOutcomeWithText(t);
							}
						}}
						onClose={() => setPullPanelOpen(false)}
					/>
				)}

				{/* AI suggestion panel */}
				{aiPanelOpen && (
					<div className="rounded-lg border border-border bg-secondary/40 p-4 mb-5">
						<div className="flex items-center justify-between mb-3">
							<div className="flex items-center gap-2 text-[13.5px] font-semibold text-primary">
								<Sparkles size={13} />
								AI-suggested outcomes
								<span className="text-[12px] font-normal text-muted-foreground">
									Based on your course description
								</span>
							</div>
							<div className="flex items-center gap-2">
								<Button type="button" size="xs" onClick={handleAddSelected}>
									Add selected
								</Button>
								<button
									type="button"
									onClick={() => setAiPanelOpen(false)}
									className="text-muted-foreground hover:text-foreground transition-colors p-1"
									aria-label="Discard suggestions"
								>
									<X size={14} />
								</button>
							</div>
						</div>
						<div className="flex flex-col gap-1.5">
							{aiSuggestions.map((text, i) => (
								<label
									key={typeof text === 'string' ? text : i}
									htmlFor={`outcome-suggestion-${i}`}
									className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left text-[13px] transition-colors cursor-pointer ${
										checkedSuggestions[i]
											? 'border-border bg-white text-foreground'
											: 'border-transparent bg-white/50 text-muted-foreground'
									}`}
								>
									<Checkbox
										id={`outcome-suggestion-${i}`}
										checked={!!checkedSuggestions[i]}
										className="mt-0.5 shrink-0"
										onCheckedChange={() =>
											setCheckedSuggestions((prev) => ({ ...prev, [i]: !prev[i] }))
										}
									/>
									{typeof text === 'string' ? text : ''}
								</label>
							))}
						</div>
					</div>
				)}

				{renderHeader()}
				{!isApprovalMode && (
					<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
						<SortableContext items={outcomeIds} strategy={verticalListSortingStrategy}>
							<form onSubmit={handleSubmit(onSubmit)}>
								<div data-testid="draggable-outcomes-container">
									{sortedOutcomes.map((outcome: CoursesOutcomesEntity, i: number) => {
										const key = outcome.courses_outcomes_id;

										return (
											<DraggableOutcome
												isDragDisabled={isApprovalMode || !canEdit}
												key={key}
												outcome={outcome}
												index={i}
											/>
										);
									})}
								</div>
							</form>
						</SortableContext>
					</DndContext>
				)}
				{isApprovalMode &&
					sortedOutcomes.map((outcome: CoursesOutcomesEntity) => {
						const key = outcome.courses_outcomes_id;
						const { outcome_text: outcomeText } = outcome.outcome;

						// Match by ID instead of array index to ensure correct comparison
						const originalOutcome = courseOutcome?.find(
							(orig: CoursesOutcomesEntity) =>
								orig.courses_outcomes_id === outcome.courses_outcomes_id
						);

						return (
							<div className="diff-outcome-wrap" key={key}>
								<CourseDiff
									original={originalOutcome?.outcome?.outcome_text ?? ''}
									draft={outcomeText}
								/>
							</div>
						);
					})}
			</FadeIn>
			{canEdit && !isApprovalMode && (
				<Button
					variant="secondary"
					type="button"
					size="small"
					className="py-4 w-fit"
					onClick={addNewOutcome}
				>
					Add New Outcome
				</Button>
			)}
		</>
	);
};

export default CourseOutcomeTab;
